import React, { useState } from "react";

import { Button, Icon, Menu, MenuItem, Position } from "@blueprintjs/core";

import { pluralize } from "@/components/helpers/string-utils";
import { Popover } from "@/components/helpers/ui/blueprint-overrides/Popover";
import { getRoleplayCompletionInstructions } from "@/components/pages_logged_in/program/components/ProgramRoleplayCard";
import { RankIcon } from "@/components/pieces/roleplays/RankIcon";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { InputMaybe, RoleplayRank } from "@/graphql";
import { cn } from "@/lib/utils";

interface RoleplayCriteriaFormInputProps {
  scenarioRequiredNum: number | null;
  scenarioAttemptMin: number | null;
  scenarioAttemptMax: number | null;
  scenarioRankMin: string | null;
  totalScenarios: number;
  onScenarioRequiredNumChange: (value: number | null) => void;
  onScenarioAttemptMinChange: (value: number | null) => void;
  onScenarioAttemptMaxChange: (value: number | null) => void;
  onScenarioRankMinChange: (value: InputMaybe<RoleplayRank> | null) => void;
}

// Custom Dropdown Component using Blueprint.js
interface DropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: {
    value: string;
    label: string;
    activeLabel?: string;
    icon?: React.ReactNode;
    disabled?: boolean;
  }[];
  defaultOption?: {
    value: string;
    label: string;
    activeLabel?: string;
    icon?: React.ReactNode;
  };
  placeholder?: string;
  className?: string;
  small?: boolean;
}

function CustomDropdown({
  value,
  onChange,
  options,
  defaultOption,
  placeholder,
  className = "",
  small = false,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption =
    options.find((opt) => opt.value === value) ?? defaultOption;

  const menuContent = (
    <Menu className="!min-w-40">
      {options.map((option) => (
        <MenuItem
          key={option.value}
          active={option.label === selectedOption?.label}
          disabled={option.disabled}
          text={
            <div
              className={cn("flex items-center gap-2", {
                "text-gray-700": !option.disabled,
                "text-gray-400": option.disabled,
                "font-semibold":
                  option.label === selectedOption?.label && !option.disabled,
              })}
            >
              {option.icon}
              <span className="whitespace-nowrap">{option.label}</span>
            </div>
          }
          onClick={() => {
            if (!option.disabled) {
              onChange(option.value);
              setIsOpen(false);
            }
          }}
        />
      ))}
    </Menu>
  );

  return (
    <div className={`relative ${className}`}>
      <Popover
        content={menuContent}
        position={Position.BOTTOM_LEFT}
        minimal={false}
        modifiers={{
          arrow: { enabled: false },
        }}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onOpened={() => setIsOpen(true)}
      >
        <Button
          rightIcon="caret-down"
          className={cn("justify-between border border-neutral-300 px-4 py-3", {
            "w-40": !small,
          })}
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="flex items-center gap-2 overflow-hidden">
            {selectedOption?.icon}
            <span className="whitespace-nowrap overflow-hidden text-ellipsis text-maintext !font-normal">
              {selectedOption?.activeLabel ||
                selectedOption?.label ||
                placeholder}
            </span>
          </div>
        </Button>
      </Popover>
    </div>
  );
}

export default function RoleplayCriteriaFormInput({
  scenarioRequiredNum,
  scenarioAttemptMin,
  scenarioAttemptMax,
  scenarioRankMin,
  totalScenarios,
  onScenarioRequiredNumChange,
  onScenarioAttemptMinChange,
  onScenarioAttemptMaxChange,
  onScenarioRankMinChange,
}: RoleplayCriteriaFormInputProps) {
  // Generate scenario required options
  const scenarioRequiredOptions = React.useMemo(() => {
    const options = [];
    for (let i = 1; i <= totalScenarios; i++) {
      options.push({
        value: i.toString(),
        label: i.toString(),
      });
    }
    return options;
  }, [totalScenarios]);

  // Generate minimum attempt options (filtered based on maximum)
  const minAttemptOptions = React.useMemo(() => {
    const options = [];
    const maxLimit = scenarioAttemptMax || 10;

    for (let i = 1; i <= 10; i++) {
      options.push({
        value: i.toString(),
        label: i.toString(),
        disabled: i > maxLimit,
      });
    }
    return options;
  }, [scenarioAttemptMax]);

  // Generate maximum attempt options (filtered based on minimum)
  const maxAttemptOptions = React.useMemo(() => {
    const options = [];
    const minLimit = scenarioAttemptMin || 1;

    for (let i = 1; i <= 10; i++) {
      options.push({
        value: i.toString(),
        label: i.toString(),
        disabled: i < minLimit,
      });
    }
    return options;
  }, [scenarioAttemptMin]);

  // Rank options with icons
  const rankOptions = [
    {
      value: "",
      label: "Any",
      icon: <RankIcon rank={RoleplayRank.Unranked} type="circle" size={18} />,
    },
    {
      value: RoleplayRank.Bronze.toString(),
      label: "Bronze (50-74%)",
      activeLabel: "Bronze",
      icon: <RankIcon rank={RoleplayRank.Bronze} type="circle" size={18} />,
    },
    {
      value: RoleplayRank.Silver.toString(),
      label: "Silver (75-89%)",
      activeLabel: "Silver",
      icon: <RankIcon rank={RoleplayRank.Silver} type="circle" size={18} />,
    },
    {
      value: RoleplayRank.Gold.toString(),
      label: "Gold (90-100%)",
      activeLabel: "Gold",
      icon: <RankIcon rank={RoleplayRank.Gold} type="circle" size={18} />,
    },
  ];

  const handleScenarioRequiredChange = (value: string) => {
    const numValue = value ? parseInt(value, 10) : null;
    onScenarioRequiredNumChange(numValue);
  };

  const handleAttemptMinChange = (value: string) => {
    const numValue = value ? parseInt(value, 10) : null;

    // If setting a minimum that's greater than current maximum, clear the maximum
    if (numValue && scenarioAttemptMax && numValue > scenarioAttemptMax) {
      onScenarioAttemptMaxChange(null);
    }

    onScenarioAttemptMinChange(numValue);
  };

  const handleAttemptMaxChange = (value: string) => {
    const numValue = value ? parseInt(value, 10) : null;

    // If setting a maximum that's less than current minimum, clear the minimum
    if (numValue && scenarioAttemptMin && numValue < scenarioAttemptMin) {
      onScenarioAttemptMinChange(null);
    }

    onScenarioAttemptMaxChange(numValue);
  };

  const handleRankMinChange = (value: string) => {
    const finalValue = value ? (value as RoleplayRank) : null;
    onScenarioRankMinChange(finalValue);
  };

  const instructions = getRoleplayCompletionInstructions({
    maximumAttempts: scenarioAttemptMax,
    minimumAttempts: scenarioAttemptMin,
    numOfRequiredScenarios: scenarioRequiredNum,
    numOfScenarios: totalScenarios,
    minimumRank: scenarioRankMin as RoleplayRank,
  });

  return (
    <div className="flex justify-center gap-3 rounded-md border border-neutral-300 p-4">
      <div className="flex flex-1 flex-col gap-4">
        {/* Scenarios Required Section */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-center gap-2.5">
            <CustomDropdown
              value={scenarioRequiredNum?.toString() || ""}
              onChange={handleScenarioRequiredChange}
              options={scenarioRequiredOptions}
              defaultOption={
                scenarioRequiredOptions[scenarioRequiredOptions.length - 1]
              }
              placeholder={totalScenarios.toString()}
              small
            />
            <div className="flex-1 text-base font-semibold">
              of{" "}
              {pluralize({
                amount: totalScenarios,
                singular: "scenario",
                plural: "scenarios",
                showCount: true,
              })}{" "}
              required for completion
            </div>
          </div>
          <div className="text-sm text-subtext">{instructions}</div>
        </div>

        {/* Criteria Section */}
        <div className="flex flex-col gap-3">
          {/* Minimum Score */}
          <div className="flex md:items-center gap-6 xs:flex-col md:flex-row">
            <div className="flex flex-1 items-center gap-2">
              <div className="text-base">Minimum score to pass</div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Icon
                    icon="small-info-sign"
                    size={16}
                    className="text-prompt"
                  />
                </TooltipTrigger>
                <TooltipContent className="text-pretty" side="bottom">
                  Sets minimum requirements of the participant's best attempt on
                  each scenario in order to pass
                </TooltipContent>
              </Tooltip>
            </div>
            <CustomDropdown
              value={scenarioRankMin || rankOptions[0].value}
              onChange={handleRankMinChange}
              options={rankOptions}
              placeholder="Any"
            />
          </div>

          {/* Minimum Attempts */}
          <div className="flex md:items-center gap-6 xs:flex-col md:flex-row">
            <div className="flex flex-1 items-center gap-2">
              <div className="text-base">Minimum number of attempts</div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Icon
                    icon="small-info-sign"
                    size={16}
                    className="text-prompt"
                  />
                </TooltipTrigger>
                <TooltipContent className="text-pretty" side="bottom">
                  Minimum number of attempts participants must make on each
                  scenario
                </TooltipContent>
              </Tooltip>
            </div>
            <CustomDropdown
              value={scenarioAttemptMin?.toString() || ""}
              onChange={handleAttemptMinChange}
              options={[{ value: "", label: "None" }, ...minAttemptOptions]}
              placeholder="None"
            />
          </div>

          {/* Maximum Attempts */}
          <div className="flex md:items-center gap-6 xs:flex-col md:flex-row">
            <div className="flex flex-1 items-center gap-2">
              <div className="text-base">Maximum number of attempts</div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Icon
                    icon="small-info-sign"
                    size={16}
                    className="text-prompt"
                  />
                </TooltipTrigger>
                <TooltipContent className="text-pretty" side="bottom">
                  Limit the number of attempts participants can make on each
                  scenario
                </TooltipContent>
              </Tooltip>
            </div>
            <CustomDropdown
              value={scenarioAttemptMax?.toString() || ""}
              onChange={handleAttemptMaxChange}
              options={[{ value: "", label: "No Limit" }, ...maxAttemptOptions]}
              placeholder="No Limit"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
